import axios from "axios";

const QUICK_VIEW_OVERLAP = 20;

const quickViewWidthFor = (windowWidth) => Math.min(520, Math.max(380, Math.round(windowWidth * 0.3)));

// Estado e carga de dados do Product Quick View (painel de giro) do winners-v2,
// compartilhados com as telas que usam ProductQuickViewVertical/Window.
// O componente que usa o mixin fornece:
//   - quickViewProduct com `internal_code` (código interno do ERP);
//   - selectedStoreId (número da loja no ERP);
//   - goToQuickViewProductAt(index) para a navegação anterior/próximo.
export const productQuickViewMixin = {
    data() {
        return {
            updatedAt: "",
            diagnosticsStores: [],
            selectedStoreId: null,
            cliId: undefined,
            apiBaseUrl: process.env.VUE_APP_MIRAI_BASE_URL || "https://api-integration.clubdacotacao.com.br/v3/intelligence",
            quickViewVisible: false,
            quickViewWindowVisible: false,
            quickViewProduct: null,
            quickViewSiblings: null,
            quickViewSiblingIndex: -1,
            quickViewReadonly: false,

            lastPurchase: {},
            stock: {},
            lastSale: {},
            salesAnalysis: [],

            chartLabels: [],
            salesPoints: [],
            purchasePoints: [],
            salesHighlights: [],
            purchaseHighlights: [],

            purchases: [],
            networkSales: [],
            networkTotal: {
                average: 0,
                total: 0,
            },
            quickViewStores: [],
            quickViewLoading: false,
            quickViewWidth: quickViewWidthFor(window.innerWidth),
        };
    },
    created() {
        window.addEventListener("resize", this.updateQuickViewWidth);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.updateQuickViewWidth);
    },
    computed: {
        quickViewOffsetStyle() {
            return { "--quick-view-width": `${this.quickViewWidth - QUICK_VIEW_OVERLAP}px` };
        },
        hasQuickViewNavigation() {
            return Array.isArray(this.quickViewSiblings) && this.quickViewSiblings.length > 1;
        },
        canGoPreviousQuickViewProduct() {
            return this.hasQuickViewNavigation && this.quickViewSiblingIndex > 0;
        },
        canGoNextQuickViewProduct() {
            return (
                this.hasQuickViewNavigation &&
                this.quickViewSiblingIndex < this.quickViewSiblings.length - 1
            );
        },
        // The intelligence API (product_metrics) is keyed by código interno —
        // products that were never offered/ordered (only reachable from the
        // Produtos tab, which lists every product regardless) commonly lack
        // one, so there's nothing to fetch. Surfacing this beats silently
        // showing an empty panel.
        quickViewDataUnavailable() {
            return !!this.quickViewProduct && !this.quickViewProduct.internal_code;
        },
    },
    methods: {
        updateQuickViewWidth() {
            this.quickViewWidth = quickViewWidthFor(window.innerWidth);
        },
        buildSalesPoints(sales) {
            return sales
                .map(item => ({
                    date: item.data,
                    value: Number(item.quantidade || 0),
                }))
                .filter(point => point.date)
                .sort((a, b) => a.date.localeCompare(b.date));
        },
        buildPurchasePoints(purchases) {
            return purchases
                .map(item => ({
                    date: item.dataentrada,
                    value: Number(item.quantidade_total || 0)
                }))
                .filter(point => point.date)
                .sort((a, b) => a.date.localeCompare(b.date));
        },
        fillMissingDays(points, startDate, endDate) {
            const totalsByDay = new Map();

            points.forEach(point => {
                const key = String(point.date).substring(0, 10);
                totalsByDay.set(key, (totalsByDay.get(key) || 0) + point.value);
            });

            const result = [];
            const cursor = new Date(startDate);
            cursor.setHours(0, 0, 0, 0);

            const end = new Date(endDate);
            end.setHours(0, 0, 0, 0);

            while (cursor <= end) {
                const key = cursor.toISOString().substring(0, 10);

                result.push({
                    date: key,
                    value: totalsByDay.get(key) || 0,
                });

                cursor.setDate(cursor.getDate() + 1);
            }

            return result;
        },
        formatChartDate(date) {
            if (!date) return '';

            const value = String(date).substring(0, 10);
            const [year, month, day] = value.split('-');

            if (!year || !month || !day) {
                return '';
            }

            return `${day}/${month}`;
        },
        buildChartLabels(sales, purchases) {
            const dates = [
                ...sales.map(item => item.data),
                ...purchases.map(
                    item => item.dataentrada
                ),
            ];

            const uniqueDates = [...new Set(
                dates
                    .filter(Boolean)
                    .map(date => String(date).substring(0, 10))
            )].sort();

            const maxLabels = 7;

            if (uniqueDates.length <= maxLabels) {
                return uniqueDates.map(date => ({
                    date,
                    label: this.formatChartDate(date),
                }));
            }

            const step = (uniqueDates.length - 1) / (maxLabels - 1);

            return Array.from({ length: maxLabels }, (_, index) => {
                const position = Math.round(index * step);
                const date = uniqueDates[position];

                return {
                    date,
                    label: this.formatChartDate(date),
                };
            });
        },
        async loadQuickViewData() {
            this.cliId = process.env.VUE_APP_MIRAI_CLI_ID || this.clientId || this.$store.state.user.cli_id;
            const productId = this.quickViewProduct.internal_code;
            const storeId = this.selectedStoreId;

            if (!productId || !storeId) {
                return;
            }
            this.quickViewLoading = true;
            try {

                const response = await axios.get(
                    `${this.apiBaseUrl}/product_metrics/${this.cliId}?id_loja=${storeId}&id_produto=${productId}`
                );

                const metrics = response.data || {};

                const sales = metrics.sales || [];
                const purchases = metrics.purchases || [];

                // Última venda
                const product = metrics.product || {};

                this.lastSale = product.last_sale
                    ? {
                        date: product.last_sale,
                        quantity: product.last_sale_quantity,
                        value: product.last_sale_value,
                    }
                    : null;

                // Estoque
                this.stock = metrics.stock

                // Últimas compras
                const sortedPurchases = [...purchases].sort(
                    (a, b) =>
                        new Date(b.dataentrada) - new Date(a.dataentrada)
                );

                this.purchases = sortedPurchases.map((purchase, index) => ({
                    key: purchase.id || index,
                    store: purchase.loja_descricao,
                    date: purchase.dataentrada,
                    packages: purchase.qtdembalagem,
                    quantity: purchase.quantidade_total,
                    supplier: purchase.nomefantasia,
                    unitValue: purchase.valor,
                    total: purchase.valortotal,
                }));

                // Última compra
                const lastPurchase = sortedPurchases[0];

                this.lastPurchase = lastPurchase
                    ? {
                        date: lastPurchase.dataentrada,
                        quantity: lastPurchase.quantidade_total,
                        packages: lastPurchase.quantidade,
                        packageSize: lastPurchase.qtdembalagem,
                        value: lastPurchase.valor,
                        supplier: lastPurchase.nomefantasia,
                        salesAfter: metrics.stock.soldSinceLastPurchase,
                        estimatedStock: metrics.stock.estimated,
                    }
                    : null;

                // Vendas

                const networkSales = metrics.network_sales || [];

                this.networkSales = networkSales.map((item, index) => ({
                    key: index + 1,
                    store: item.loja_descricao,
                    average: item.average,
                    total: item.quantidade_total_30d,
                    tendency: item.tendencia_venda,
                    giroPeriods: [
                        { label: 'Últimos 180 dias', giro: item.giro_medio, quantity: item.quantidade_venda },
                        { label: 'Últimos 90 dias', giro: item.giro_medio_90d, quantity: item.quantidade_total_90d },
                        { label: 'Últimos 30 dias', giro: item.giro_medio_30d, quantity: item.quantidade_total_30d },
                        { label: 'Últimos 14 dias', giro: item.giro_medio_14d, quantity: item.quantidade_total_14d },
                        { label: 'Últimos 7 dias', giro: item.giro_medio_7d, quantity: item.quantidade_total_7d },
                    ],
                }));

                this.networkTotal = metrics.network_total || {
                    average: 0,
                    total: 0,
                };

                // Gráfico

                const chartEnd = new Date();
                const chartStart = new Date();
                chartStart.setMonth(chartStart.getMonth() - 3);

                this.chartLabels = this.buildChartLabels(sales, purchases);

                this.salesPoints = this.fillMissingDays(
                    this.buildSalesPoints(sales),
                    chartStart,
                    chartEnd
                );

                this.purchasePoints = this.fillMissingDays(
                    this.buildPurchasePoints(purchases),
                    chartStart,
                    chartEnd
                );

                // Lojas

                this.diagnosticsStores = metrics.stores || []

                this.salesAnalysis = (metrics.sales_analysis || []).map(item => ({
                    period: `Últimos ${item.days} dias`,
                    average: item.average,
                    total: item.total,
                }));

                this.updatedAt = metrics.updatedAt;

            } catch (error) {
                console.error('Erro ao carregar dados do Quick View:', error);
            } finally {
                this.quickViewLoading = false;
            }
        },
        closeProductQuickView() {
            this.quickViewVisible = false;
        },
        async goToPreviousQuickViewProduct() {
            if (!this.canGoPreviousQuickViewProduct) {
                return;
            }

            await this.goToQuickViewProductAt(this.quickViewSiblingIndex - 1);
        },
        async goToNextQuickViewProduct() {
            if (!this.canGoNextQuickViewProduct) {
                return;
            }

            await this.goToQuickViewProductAt(this.quickViewSiblingIndex + 1);
        },
        openQuickViewWindow() {
            this.quickViewVisible = false;
            this.quickViewWindowVisible = true;
        },
        closeQuickViewWindow() {
            this.quickViewWindowVisible = false;
        },
        collapseQuickViewWindow() {
            this.quickViewWindowVisible = false;
            this.quickViewVisible = true;
        },
    },
};
